import * as THREE from "three";
import { disposeMaterial } from "utils/functions.utils";
import { IObject, ObjectTypes } from "utils/interface.utils";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { cleanupTransformBox, renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";

export const createObjectImage = async (workspace: WorkspaceContext, objectId: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, loaderRef, targetImageRef, objectsRef, selectedObjectIDRef, selectedTransformControlRef } = workspace.refs;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !loaderRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const loader = loaderRef.current;
    const targetImageGroup = targetImageRef.current;

    // Clean up existing object if it exists
    if (objectsRef.current.has(objectId)) {
        const existingObject = objectsRef.current.get(objectId);
        if (existingObject?.cleanup) {
            existingObject.cleanup();
        }
        objectsRef.current.delete(objectId);
    }

    try {
        const objectImage = await renderObjectImage(workspace, scene, loader, properties, objectId, targetImageGroup)

        objectsRef.current.set(objectId, {
            mesh: objectImage.mesh,
            cleanup: objectImage.cleanup
        });

        // A newly added object is selected with the Position tool already active; the tool
        // effect ran before this mesh existed (textures load async), so attach its box here.
        if (objectId === selectedObjectIDRef.current && selectedTransformControlRef.current) {
            renderTransformBox(workspace, objectImage.mesh);
        }

        if (rendererRef.current) {
            rendererRef.current.render(scene, camera);
        }

    } catch (error) {
        console.error("Error creating object image:", error);
    }
}

export const updateObjectImage = (workspace: WorkspaceContext, objectId: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, loaderRef, rotationSystemRef, scaleSystemRef, objectsRef, allObjectsPreviousScaleRef } = workspace.refs;
    if (rotationSystemRef.current?.isObjectBeingDraggedAndRotated(objectId)) return;
    if (scaleSystemRef.current?.isObjectBeingDraggedAndScaled(objectId)) return;
    
    const { src, positionX, positionY, rotationX, scaleX, scaleY, scaleZ, renderOrder } = properties;
    
    const objectData = objectsRef.current.get(objectId);
    if (!objectData || !objectData.mesh || !loaderRef.current) return;

    const mesh = objectData.mesh as THREE.Mesh;
    const objectImage = mesh.children[0] as THREE.Mesh;

    if (positionX !== undefined && mesh.position.x !== positionX) mesh.position.x = positionX;
    if (positionY !== undefined && mesh.position.y !== positionY) mesh.position.y = positionY;

    if (mesh.userData.transformBox) {
        mesh.userData.transformBox.position.set(positionX || 0, positionY || 0, 0);
    }

    if (rotationX !== undefined) {
        const rotationXRadians = THREE.MathUtils.degToRad(rotationX);
        if (mesh.rotation.z !== rotationXRadians) {
            mesh.rotation.z = rotationXRadians;
        }

        if (mesh.userData.transformBox) {
            mesh.userData.transformBox.rotation.z = rotationXRadians;
        }
    }

    // Update scale
    const getPreviousObjectScale = allObjectsPreviousScaleRef.current.find((object: { ID: string | null, scale: { x: number, y: number, z: number } }) => object.ID === objectId);

    // If we have a valid previous scale reference
    if (getPreviousObjectScale?.scale) {
        let shouldUpdateTransformBox = false;
        // Update x scale if provided AND changed
        if (scaleX !== undefined && scaleX !== getPreviousObjectScale.scale.x) {
            mesh.scale.x = Math.max(scaleX, 0.001);
            getPreviousObjectScale.scale.x = scaleX;
            shouldUpdateTransformBox = true;
        }

        // Update y scale if provided AND changed
        if (scaleY !== undefined && scaleY !== getPreviousObjectScale.scale.y) {
            mesh.scale.y = Math.max(scaleY, 0.001);
            getPreviousObjectScale.scale.y = scaleY;
            shouldUpdateTransformBox = true;
        }

        // Update z scale if provided AND changed
        if (scaleZ !== undefined && scaleZ !== getPreviousObjectScale.scale.z) {
            mesh.scale.z = Math.max(scaleZ, 0.001);
            getPreviousObjectScale.scale.z = scaleZ;
            shouldUpdateTransformBox = true;
        }

        if (shouldUpdateTransformBox && mesh.userData.transformBox) {
            renderTransformBox(workspace, mesh);
        }
    }

    // Update the render order
    if (renderOrder !== undefined) {
        mesh.renderOrder = renderOrder;
        mesh.userData.renderOrder = renderOrder;

        // Force renderer update to ensure proper layering
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
    }

    if (src && objectImage.material instanceof THREE.MeshBasicMaterial) {
        const material = objectImage.material as THREE.MeshBasicMaterial;
        const currentSrc = objectImage.userData.currentSrc;

        if (currentSrc !== src) {
            loaderRef.current.load(
                src,
                (newTexture) => {
                    if (material.map) {
                        material.map.dispose();
                    }

                    material.map = newTexture;
                    material.needsUpdate = true;
                    objectImage.userData.currentSrc = src;
                },
                undefined,
                (error) => {
                    console.error("Error replacing object image:", error);
                }
            )
        }
    }
}

export const renderObjectImage = (workspace: WorkspaceContext, scene: THREE.Scene, loader: THREE.TextureLoader, properties: IObject, objectID: string, targetImageGroup: THREE.Group | null): Promise<{ mesh: THREE.Object3D, cleanup: () => void }> => {
    const { rendererRef, sceneRef, cameraRef, allObjectsPreviousScaleRef } = workspace.refs;
    return new Promise((resolve, reject) => {
        const { src, positionX, positionY, rotationX, scaleX, scaleY, scaleZ, renderOrder } = properties;
        const parentObject = targetImageGroup || scene;
        loader.load(
            src,
            (texture) => {
                const objectGeometry = new THREE.PlaneGeometry(1, 1);
                const objectMaterial = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
                const objectImage = new THREE.Mesh(objectGeometry, objectMaterial);

                const imageGroup = new THREE.Group();
                imageGroup.position.set(positionX || 0, positionY || 0, 0);
                imageGroup.scale.set(scaleX, scaleY, scaleZ);
                
                const initialRotation = THREE.MathUtils.degToRad(rotationX || 0);
                imageGroup.rotation.set(0, 0, initialRotation);
                
                imageGroup.renderOrder = renderOrder;
                
                imageGroup.add(objectImage);

                allObjectsPreviousScaleRef.current.push({
                    ID: objectID,
                    scale: {
                        x: scaleX,
                        y: scaleY,
                        z: scaleZ
                    }
                })
                
                // Set user data
                imageGroup.userData.isDraggable = true;
                imageGroup.userData.initialRotation = initialRotation;
                imageGroup.userData.id = objectID;
                imageGroup.userData.objectType = ObjectTypes.IMAGE;
                objectImage.userData.currentSrc = src;
                imageGroup.userData.renderOrder = renderOrder;
                // Store initial dimensions
                imageGroup.userData.originalWidth = scaleX;
                imageGroup.userData.originalHeight = scaleY;

                // Add to parent group instead of directly to scene
                const existingChild = parentObject.children.find(child => child.userData.id === objectID);
                if (existingChild) {
                    parentObject.remove(existingChild);
                }
                parentObject.add(imageGroup);

                imageGroup.visible = true;

                if (rendererRef.current && sceneRef.current && cameraRef.current) {
                    rendererRef.current.render(sceneRef.current, cameraRef.current);
                }

                // Create and return the object with cleanup function
                resolve({
                    mesh: imageGroup,
                    cleanup: () => {
                        // The transform box is a sibling of the object, so removing the object alone leaves it on the canvas.
                        cleanupTransformBox(workspace, imageGroup);
                        const existingChild = parentObject.children.find(child => child.userData.id === objectID);
                        if (existingChild) {
                            parentObject.remove(existingChild);
                        }

                        // Dispose of geometry
                        if (objectGeometry) objectGeometry.dispose();

                        if (objectMaterial) {
                            if (objectMaterial.map) {
                                objectMaterial.map.dispose();
                            }
                            disposeMaterial(objectMaterial);
                        }

                        if (texture) {
                            texture.dispose();
                        }

                        if (imageGroup.userData) {
                            Object.keys(imageGroup.userData).forEach(key => {
                                imageGroup.userData[key] = null;
                            });
                        }
                    }
                });
            },
            undefined,
            (error) => {
                console.error("Object image loading error:", error);
                reject(error);
            }
        );
    });
};
